import { setShown, getById, querySelectorAll } from './util/dom';
import { assert } from './util/assert';
import * as db from './util/db';
import JSZip from 'jszip';
import { getRouteImageUrl, RouteData } from './util/route-data';
import { registerServiceWorker } from './util/service-worker-client';
import { importFile, importFromUrl, refreshRoute, saveRoute } from './import/import-route-data';
import { requestPersistedStorage, setStatus } from './import/import-utils';
import { extractImagesFromKmzZip } from './import/import-kmz';
import { supportsRefresh } from './import/route-refresh-support';

let currentData: RouteData | undefined;

/** Redirect to the imported route when the import completes. */
const REDIRECT_WHEN_FINISHED = location.hostname !== 'localhost';

async function main() {
  try {
    registerServiceWorker();
  } catch (e: any) {
    const message = (e.message ?? `${e}`) as string;
    showUnsupported(message);
    return;
  }

  // This is an issue in Safari where on first load, it won't use the service worker.
  if (!navigator.serviceWorker.controller) {
    setShown('loading', false);
    setShown('refresh-required', true);
    return;
  }

  const anyWindow = window as any;
  if (!(window.indexedDB || anyWindow['mozIndexedDB'] || anyWindow['webkitIndexedDB'] || anyWindow['msIndexedDB'])) {
    showUnsupported('Your device is unsupported. indexDB support is required.');
    return;
  }

  currentData = await db.get();

  setShown('loading', false);
  setShown('page', true);

  if (maybeSetupSharePage()) {
    return;
  }

  if (currentData) {
    setShown('current-route', true);
    getById('current-route-name').textContent = currentData.name;

    if (supportsRefresh(currentData)) {
      if (location.hash === '#refresh') {
        // Special case of just starting the refresh from a link, don't show the
        // full page.
        requestAnimationFrame(() => startRefresh());
        setShown('import-route', false);
        return;
      }
      setShown('refresh-route', true);
      getById('refresh-route-button').addEventListener('click', startRefresh);
    }
  }

  // Setup Load Existing
  for (const button of Array.from(querySelectorAll('.load-existing-button'))) {
    button.addEventListener('click', async (event) => {
      loadExisting(event.target as HTMLButtonElement);
    });
  }

  // Setup Importing
  getById('start-import').addEventListener('click', async (event) => {
    const button = event.target as HTMLButtonElement;

    const files = (getById('file-input') as HTMLInputElement).files ?? [];
    if (files.length) {
      const file = files[0] as File;
      runImportVia(button, `Importing ${file.name}`, () => importFile(file, { filename: file.name }));
      return;
    }

    const url = (getById('import-url') as HTMLInputElement).value.trim();
    if (url) {
      runImportVia(button, 'Loading URL', () => importFromUrl(url));
      return;
    }

    alert('Please provide a URL or select a file.');
  });
}

function maybeSetupSharePage(): boolean {
  if (location.hash.indexOf('url=') === -1) return false;

  const params = new URLSearchParams(location.hash.substring(1));
  const url = params.get('url');
  const name = params.get('name');
  if (!url || !name) return false;

  setShown('import-from-share', true);
  setShown('load-existing', false);
  setShown('current-route', false);
  setShown('import-route', false);

  getById('share-import-name').textContent = name;
  getById('share-import-link').setAttribute('href', url);
  getById('share-import-link').textContent = url;
  setShown('replace-route-warning', !!currentData);
  if (currentData) {
    getById('current-route-name2').textContent = currentData.name;
  }

  getById('start-share-import').addEventListener('click', event => {
    runImportVia(event.target as HTMLButtonElement, 'Loading Map URL',
      () => importFromUrl(url));
  });

  return true;
}

async function loadExisting(button: HTMLButtonElement) {
  // Reset state set by showLoadExistingError.
  setShown('load-existing-error', false);
  button.disabled = true;
  button.textContent = 'Loading...';

  try {
    await requestPersistedStorage();

    const zipUrl = `/routes/${button.dataset.zipFile}`;
    const response = await fetch(zipUrl);
    if (!response.ok) {
      showLoadExistingError(button, `Received HTTP ${response.status}`);
    }
    const file = (await response.arrayBuffer());
    const zip = await JSZip.loadAsync(file);
    const routeJsonFile = zip.files['route.json'];
    assert(routeJsonFile, 'route.json not found in zip');
    const routeData = JSON.parse(await routeJsonFile.async('string')) as RouteData;
    const images = await extractImagesFromKmzZip(routeData, zip);
    await saveRoute({ routeData, images });
    if (REDIRECT_WHEN_FINISHED) {
      location.href = '/';
    }
  } catch (error) {
    showLoadExistingError(button, error instanceof Error ? error.message : `${error}`);
  }
}

function showLoadExistingError(button: HTMLButtonElement, message: string) {
  setShown('load-existing-error', true);
  getById('load-existing-error-message').textContent = `Failed to load route. ${message}`;
  button.disabled = false;
  button.textContent = 'Load Route';
}

async function runImportVia(button: HTMLButtonElement | undefined, initialStatus: string, importFn: (() => Promise<void>)) {
  setStatus(initialStatus);

  if (button) {
    button.classList.add('animate-out');
    button.onanimationend = () => {
      setImportingShown(true);
    };
  } else {
    setImportingShown(true);
  }

  requestAnimationFrame(async () => {
    try {
      await importFn();

      setStatus('Processing complete. Loading route');
      if (REDIRECT_WHEN_FINISHED) {
        location.href = '/';
      }
    } catch (error: any) {
      console.error(error);
      setImportingShown(false);
      setShown('import-error', true);
      const msg = error.message || error;
      getById('import-error-message').textContent = msg;
    }
  });
}

async function startRefresh(event?: Event) {
  runImportVia(event?.target as HTMLButtonElement | undefined, 'Refreshing',
    () => refreshRoute(currentData!));
}

function showUnsupported(msg: string) {
  setShown('loading', false);
  setShown('page', false);
  setShown('unsupported', true);
  getById('unsupported-message').textContent = msg;
}

function setImportingShown(show: boolean) {
  setShown('importing', show);
  setShown('import-form', !show);
  if (show) {
    setShown('import-error', false);
  } else {
    // Clear importing status.
    getById('import-status').innerHTML = '';
  }
  for (const el of Array.from(document.querySelectorAll('.animate-out'))) {
    const button = el as HTMLButtonElement;
    button.onanimationend = null;
    button.classList.remove('animate-out');
  }
}


/**
 * This is an internal tool that I use to create the zip files in routes/.
 * After importing a route, call window.saveAsJsonZip() to download the zip file.
 */
async function saveAsJsonZip() {
  const kmzData = await db.get();
  if (!kmzData) {
    console.error('No kmzData currently loaded.');
    return;
  }

  const zip = new JSZip();
  zip.file('route.json', JSON.stringify(kmzData, undefined, 2))

  const imagePaths = new Set(Object.values(kmzData.icons));
  for (const imagePath of imagePaths) {
    const image = await fetch(getRouteImageUrl(imagePath));
    const imageBlob = await image.blob();
    zip.file(imagePath, imageBlob);
  }
  const zipBlob = await zip.generateAsync({ type: 'blob' });
  saveBlob(zipBlob, kmzData.name.toLocaleLowerCase().replace(/ /g, '_') + '.zip');
}

const saveBlob = (function () {
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.setAttribute('style', 'display: none');

  return function (blob: Blob, fileName: string) {
    const url = window.URL.createObjectURL(blob);
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };
})();

(window as any)['saveAsJsonZip'] = saveAsJsonZip;

main();